/**
 * Modelo de capacidad de una sede CALE — modo "Red nacional CALE".
 *
 * Fórmulas documentadas:
 * - capacidadTeorica = salas × puestosPorSala × sesionesDia × diasHabiles
 *   (aspirantes por periodo para el examen teórico).
 * - capacidadPractica = capacidadTeorica × factorOcupacion.
 *   factorOcupacion 0.70–0.85 [DISEÑO] (escenarios bajo/medio/alto).
 * - Sesiones cada 90 minutos por sala [NORMATIVO — Res. 20253040037125 de
 *   2025, Anexo 60: sesiones teóricas cada 90 min, incluyen alistamiento,
 *   ingreso y validación de identidad].
 *
 * Funciones puras: sin efectos laterales ni dependencias externas.
 */

/** Duración de una sesión teórica en minutos [NORMATIVO Anexo 60]. */
export const DURACION_SESION_MIN = 90;

/** Jornada operativa diaria de referencia: 6:00–22:00 [NORMATIVO Anexo 60]. */
export const JORNADA_DIARIA_MIN = 16 * 60;

export interface ParametrosCapacidad {
  /** Número de salas teóricas de la sede. */
  salas: number;
  /** Puestos de examen (cubículos) por sala. */
  puestosPorSala: number;
  /** Sesiones por sala y día. Si se omite, se deriva de la jornada (90 min/sesión). */
  sesionesDia?: number;
  /** Días hábiles del periodo de análisis (p. ej. 20 para un mes). */
  diasHabiles: number;
}

export type EscenarioSensibilidad = "bajo" | "medio" | "alto";

/** Factores de ocupación por escenario [DISEÑO]. */
export const FACTORES_OCUPACION: Record<EscenarioSensibilidad, number> = {
  bajo: 0.7,
  medio: 0.775,
  alto: 0.85,
};

/** Sesiones que caben en una jornada dada (floor, sesiones de 90 min). */
export function sesionesPorDia(jornadaMin: number = JORNADA_DIARIA_MIN): number {
  return Math.floor(jornadaMin / DURACION_SESION_MIN);
}

/**
 * Capacidad teórica (aspirantes por periodo).
 * capacidadTeorica = salas × puestos × sesionesDia × diasHabiles
 */
export function capacidadTeorica(params: ParametrosCapacidad): number {
  const sesiones = params.sesionesDia ?? sesionesPorDia();
  return params.salas * params.puestosPorSala * sesiones * params.diasHabiles;
}

/** Capacidad práctica = teórica × factorOcupacion. */
export function capacidadPractica(
  params: ParametrosCapacidad,
  factorOcupacion: number,
): number {
  return capacidadTeorica(params) * factorOcupacion;
}

export interface ResultadoSensibilidad {
  escenario: EscenarioSensibilidad;
  factorOcupacion: number;
  capacidadPractica: number;
}

/**
 * Sensibilidad del modelo: tres escenarios ordenados
 * bajo ≤ medio ≤ alto, sobre la misma capacidad teórica.
 */
export function sensibilidadCapacidad(
  params: ParametrosCapacidad,
): ResultadoSensibilidad[] {
  const escenarios: EscenarioSensibilidad[] = ["bajo", "medio", "alto"];
  return escenarios.map((escenario) => ({
    escenario,
    factorOcupacion: FACTORES_OCUPACION[escenario],
    capacidadPractica: capacidadPractica(params, FACTORES_OCUPACION[escenario]),
  }));
}
